// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useParams, useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import { Tabs, TabList, Tab, Tag, Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import NotFound from '../../NotFound';
import { usePlanDetail } from '../../../hooks/usePlanDetail';
import { PageHead, Loading, EmptyState } from '../../../components/shell/ScreenHelpers';
import { PlanDetailContext } from './PlanDetailContext';
import PlanOverview from './PlanOverview';
import PlanAssets from './PlanAssets';
import PlanSavings from './PlanSavings';
import PlanExecution from './PlanExecution';
import PlanAudit from './PlanAudit';
import './PlanDetail.css';

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const STATUS_TAG_TYPE: Record<string, 'blue' | 'green' | 'red'> = {
  PENDING: 'blue',
  APPROVED: 'green',
  REJECTED: 'red',
};

// Tab order must match the routed panels below.
const TAB_KEYS = ['overview', 'assets', 'kpis', 'execution', 'audit'] as const;
const TAB_LABELS: Record<(typeof TAB_KEYS)[number], string> = {
  overview: 'Overview',
  assets: 'Assets',
  kpis: 'KPIs',
  execution: 'Execution',
  audit: 'Audit',
};

function PlanDetail() {
  const { approvalId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { plan, planFile, loading, error, updateStatus } = usePlanDetail(approvalId);

  const basePath = `/optimizations/${approvalId}`;

  const getActiveIndex = (): number => {
    const path = location.pathname;
    if (path.endsWith('/assets')) return 1;
    if (path.endsWith('/kpis')) return 2;
    if (path.endsWith('/execution')) return 3;
    if (path.endsWith('/audit')) return 4;
    return 0;
  };

  const handleTabChange = ({ selectedIndex }: { selectedIndex: number }) => {
    const key = TAB_KEYS[selectedIndex];
    navigate(key === 'overview' ? basePath : `${basePath}/${key}`);
  };

  // Prefer history-back so users return to wherever they came from
  // (/home/optimizations, the SOP "View" link, …) rather than being forced to
  // the role-gated /optimizations list. Only fall back to the list when the app
  // was opened directly on this URL (no in-app history to go back to).
  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate('/optimizations'));

  const backLink = (
    <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={goBack}>
      Back
    </Button>
  );

  if (loading) {
    return (
      <div className="hs-page">
        <PageHead title="Loading plan…" actions={backLink} />
        <Loading label="Loading plan details…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="hs-page">
        <PageHead title="Failed to load plan" actions={backLink} />
        <EmptyState
          big="Couldn't load this plan."
          sub={typeof error === 'string' ? error : 'An unexpected error occurred.'}
        />
      </div>
    );
  }

  if (!plan || !planFile) {
    return <NotFound />;
  }

  return (
    <PlanDetailContext.Provider value={{ plan, planFile, onStatusUpdated: updateStatus }}>
      <div className="hs-page">
        <div className="sched-detail-head">
          <PageHead
            title={planFile.title}
            dateline={`${plan.dc_site_id} · ${plan.date}`}
            actions={
              <>
                <Tag type={STATUS_TAG_TYPE[plan.status] ?? 'gray'} size="md">
                  {STATUS_LABELS[plan.status] ?? plan.status}
                </Tag>
                {backLink}
              </>
            }
          />
        </div>

        <Tabs selectedIndex={getActiveIndex()} onChange={handleTabChange}>
          <TabList aria-label="Plan sections" contained>
            {TAB_KEYS.map(key => (
              <Tab key={key}>{TAB_LABELS[key]}</Tab>
            ))}
          </TabList>
        </Tabs>

        <div className="sched-tab-content">
          <Routes>
            <Route path="/" element={<PlanOverview />} />
            <Route path="assets" element={<PlanAssets />} />
            <Route path="kpis" element={<PlanSavings />} />
            <Route path="execution" element={<PlanExecution />} />
            <Route path="audit" element={<PlanAudit />} />
          </Routes>
        </div>
      </div>
    </PlanDetailContext.Provider>
  );
}

export default PlanDetail;
